import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, type ModerationReport, type Surface } from "@gwrctl/api-client";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { useToast } from "../lib/toast.tsx";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import {
  Banner,
  Button,
  Card,
  Chips,
  ConfirmButton,
  Dot,
  Empty,
  ErrorState,
  Field,
  Tile,
} from "../components/Ui.tsx";

/**
 * Moderasyon.
 *
 * Yaptırımlar hafiften ağıra: gizle → şikâyeti kapat → uyar → askıya al →
 * tüm yorumlarını gizle → sil. Geri alınamaz olanlar TOTP ister.
 *
 * Askı gerçek: MoviePort veritabanında BEFORE INSERT trigger'ı yorum yazmayı
 * reddediyor (title/episode/activity). Uyarı da gerçek: kullanıcının uygulama
 * içi bildirim kutusuna düşüyor.
 */

const TABS = [
  { id: "queue", label: "Kuyruk" },
  { id: "suspensions", label: "Askılar" },
  { id: "rules", label: "Kurallar" },
  { id: "log", label: "Günlük" },
] as const;

const SURFACES = [
  { id: "title", label: "Film/Dizi" },
  { id: "episode", label: "Bölüm" },
  { id: "activity", label: "Akış" },
] as const;

type Tab = (typeof TABS)[number]["id"];

const SURFACE_IDS = SURFACES.map((sf) => sf.id);
const DAY_OPTIONS = ["1", "7", "30", "0"] as const;
const DAY_LABELS = { "1": "1 gün", "7": "7 gün", "30": "30 gün", "0": "kalıcı" };
const SURFACE_LABELS = Object.fromEntries(SURFACES.map((sf) => [sf.id, sf.label])) as Record<Surface, string>;

export function ModerationPage(): ReactNode {
  const [tab, setTab] = useState<Tab>("queue");
  const [kind, setKind] = useState<Surface>("title");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();

  const schema = useQuery({
    queryKey: ["moderation-schema"],
    queryFn: () => client.moderationSchema(),
    retry: false,
  });
  const counts = useQuery({
    queryKey: ["moderation-counts"],
    queryFn: () => client.moderationCounts(),
    retry: false,
  });
  const queue = useQuery({
    queryKey: ["moderation", kind],
    queryFn: () => client.moderationReports(kind),
    retry: false,
  });
  const { data, error } = queue;
  const searchResults = useQuery({
    queryKey: ["moderation-search", kind, search],
    queryFn: () => client.moderationSearch(search, kind),
    enabled: search.length >= 2,
  });

  const refreshAll = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["moderation"] });
    void queryClient.invalidateQueries({ queryKey: ["moderation-counts"] });
    void queryClient.invalidateQueries({ queryKey: ["moderation-user"] });
  };

  if (error instanceof ApiError && error.code === "MODERATION_UNAVAILABLE") {
    return (
      <>
        <PageHeader kicker="yönetim" title="Moderasyon" />
        <PageBody>
          <Banner kicker="kapalı">{error.message}</Banner>
        </PageBody>
      </>
    );
  }

  const reports = data?.reports ?? [];
  const current: ModerationReport | undefined =
    reports.find((r) => r.id === selected) ?? reports[0];

  return (
    <>
      <PageHeader
        kicker="yönetim"
        title="Moderasyon"
        actions={
          // Sekme seçimi birincil eylem değil; hepsi mavi düğme olunca asıl eylem kayboluyordu.
          <Chips
            values={TABS.map((t) => t.id)}
            value={tab}
            onChange={setTab}
            labels={Object.fromEntries(
              TABS.map((t) => [
                t.id,
                t.label +
                  (t.id === "queue" && counts.data?.total ? ` · ${counts.data.total}` : "") +
                  (t.id === "suspensions" && counts.data?.suspended ? ` · ${counts.data.suspended}` : ""),
              ]),
            )}
          />
        }
      />

      <PageBody>
        {schema.data && !schema.data.moderationTables && (
          <Banner kicker="kurulum">
            Moderasyon tabloları (user_suspensions, moderation_actions) bulunamadı — uyarı ve
            askı işlemleri çalışmaz. <code className="mono">ops/sql/movieport-moderation.sql</code>{" "}
            uygulanmalı.
          </Banner>
        )}

        {counts.isError && (
          <Card>
            <ErrorState compact error={counts.error} onRetry={() => void counts.refetch()} />
          </Card>
        )}

        <div className="grid gap-3 sm:grid-cols-3">
          {/* Sayılar okunamadıysa yeşil "0" göstermek yanlış güven verir. */}
          <Tile
            label="Açık şikâyet"
            value={counts.isError ? "?" : (counts.data?.total ?? "…")}
            note={
              counts.data
                ? `film/dizi ${counts.data.title} · bölüm ${counts.data.episode} · akış ${counts.data.activity ?? 0}`
                : counts.isError
                  ? "okunamadı"
                  : "yükleniyor"
            }
            tone={counts.isError ? "danger" : counts.data?.total ? "warn" : counts.data ? "ok" : "idle"}
          />
          <Tile
            label="Askıda"
            value={counts.isError ? "?" : (counts.data?.suspended ?? "…")}
            note="yorum yazamıyor"
            tone={counts.isError ? "danger" : counts.data?.suspended ? "warn" : counts.data ? "ok" : "idle"}
          />
          <Tile
            label="Yüzey"
            value={SURFACES.find((sf) => sf.id === kind)?.label ?? kind}
            note="kuyruk bu yüzeyi gösteriyor"
          />
        </div>

        {tab === "queue" && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Chips
                values={SURFACE_IDS}
                value={kind}
                labels={SURFACE_LABELS}
                onChange={(id) => {
                  setKind(id);
                  setSelected(null);
                  setPicked(new Set());
                }}
              />
              <input
                className="input w-full sm:!w-[280px]"
                aria-label="Yorumlarda ara"
                placeholder="yorumlarda ara (en az 2 karakter)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {search.length >= 2 ? (
              <Card
                title={`Arama · "${search}"`}
                hint={searchResults.data ? `${searchResults.data.results.length} sonuç` : undefined}
                flush
              >
                {searchResults.isError ? (
                  <ErrorState error={searchResults.error} onRetry={() => void searchResults.refetch()} />
                ) : searchResults.isLoading ? (
                  <Empty>Aranıyor…</Empty>
                ) : searchResults.data?.results.length ? (
                  searchResults.data.results.map((c) => (
                    <div key={c.id} className="row items-start gap-3">
                      <Dot tone={c.hidden ? "idle" : "ok"} />
                      <div className="grid min-w-0 flex-1 gap-1">
                        <span className="text-sm break-words text-pretty text-neutral-200">{c.body}</span>
                        <span className="text-2xs text-neutral-600">
                          {c.display_name ?? c.author_name ?? "—"}
                          {c.username ? ` · @${c.username}` : ""} ·{" "}
                          {new Date(c.created_at).toLocaleString("tr-TR")}
                          {c.hidden ? " · gizli" : ""}
                        </span>
                      </div>
                      <UserLink userId={c.user_id} />
                    </div>
                  ))
                ) : (
                  <Empty>Sonuç yok</Empty>
                )}
              </Card>
            ) : (
              <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
                <Card
                  title="Kuyruk"
                  hint={data ? `${reports.length} kayıt` : undefined}
                  action={
                    picked.size > 0 && (
                      <BulkBar
                        kind={kind}
                        ids={[...picked]}
                        hiddenSupported={data?.hiddenSupported ?? false}
                        onDone={() => {
                          setPicked(new Set());
                          refreshAll();
                        }}
                      />
                    )
                  }
                  flush
                >
                  {queue.isError ? (
                    <ErrorState error={queue.error} onRetry={() => void queue.refetch()} />
                  ) : queue.isLoading ? (
                    <Empty>Kuyruk yükleniyor…</Empty>
                  ) : reports.length ? (
                    reports.map((report) => {
                      const active = current?.id === report.id;
                      // Seçim kutusu ile satır ayrı öğeler: düğmenin içine input konamaz;
                      // eskiden div+onClick olduğu için klavyeyle seçilemiyordu.
                      return (
                        <div
                          key={report.id}
                          className={`flex items-start border-b border-neutral-900 last:border-b-0 ${
                            active
                              ? "border-l-2 border-l-accent bg-accent-900"
                              : "row-hover border-l-2 border-l-transparent"
                          }`}
                        >
                          <input
                            type="checkbox"
                            aria-label="Toplu işlem için seç"
                            checked={picked.has(report.id)}
                            onChange={(e) => {
                              const next = new Set(picked);
                              if (e.target.checked) next.add(report.id);
                              else next.delete(report.id);
                              setPicked(next);
                            }}
                            className="mt-[15px] ml-4 size-3.5 shrink-0 accent-[var(--color-accent)]"
                          />
                          <button
                            type="button"
                            aria-pressed={active}
                            onClick={() => setSelected(report.id)}
                            className="grid min-w-0 flex-1 cursor-pointer gap-1.5 px-3 py-3 text-left"
                          >
                            <span className="flex flex-wrap items-center gap-2">
                            <span
                              className={`text-2xs ${active ? "text-accent-300" : "text-neutral-500"}`}
                            >
                              {report.report_count} şikâyet
                            </span>
                            {report.hidden && <span className="tag tag-neutral">gizli</span>}
                            {report.user_suspended && (
                              <span className="tag" style={{ background: "rgba(248,113,113,0.15)", color: "var(--color-danger)" }}>
                                askıda
                              </span>
                            )}
                            {report.user_warnings > 0 && (
                              <span className="tag" style={{ background: "rgba(251,191,36,0.15)", color: "var(--color-warn)" }}>
                                {report.user_warnings} uyarı
                              </span>
                            )}
                            <span className="ml-auto text-2xs text-neutral-600">
                              {new Date(report.last_reported_at).toLocaleString("tr-TR", {
                                day: "2-digit",
                                month: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                            </span>
                            <span
                              className={`line-clamp-2 break-words text-pretty ${active ? "text-neutral-100" : "text-neutral-300"}`}
                            >
                              {report.body}
                            </span>
                            <span className="text-2xs text-neutral-600">
                              {report.display_name ?? report.author_name ?? "—"}
                              {report.username ? ` · @${report.username}` : ""}
                            </span>
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <Empty>Bekleyen şikâyet yok</Empty>
                  )}
                </Card>

                {current ? (
                  <ReviewPanel
                    report={current}
                    kind={kind}
                    hiddenSupported={data?.hiddenSupported ?? false}
                    onChanged={() => {
                      setSelected(null);
                      refreshAll();
                    }}
                  />
                ) : (
                  <Card title="İnceleme" flush>
                    <Empty>
                      {queue.isError ? "Kuyruk okunamadı" : queue.isLoading ? "Yükleniyor…" : "Kuyruk boş"}
                    </Empty>
                  </Card>
                )}
              </div>
            )}
          </>
        )}

        {tab === "suspensions" && <SuspensionsTab onChanged={refreshAll} />}
        {tab === "rules" && <RulesTab />}
        {tab === "log" && <LogTab />}
      </PageBody>
    </>
  );
}

function UserLink({ userId }: { userId: string }): ReactNode {
  return (
    <span className="mono ml-auto shrink-0 text-2xs text-neutral-700" title={`Kullanıcı kimliği: ${userId}`}>
      {userId.slice(0, 8)}
    </span>
  );
}

/** İnceleme paneli: içerik + kullanıcı bağlamı + tüm yaptırımlar. */
function ReviewPanel({
  report,
  kind,
  hiddenSupported,
  onChanged,
}: {
  report: ModerationReport;
  kind: Surface;
  hiddenSupported: boolean;
  onChanged: () => void;
}): ReactNode {
  const [totp, setTotp] = useState("");
  const [reason, setReason] = useState("");
  const [days, setDays] = useState(7);
  const [error, setError] = useState<string | null>(null);

  const user = useQuery({
    queryKey: ["moderation-user", report.user_id],
    queryFn: () => client.moderationUser(report.user_id),
  });

  function guard(needsTotp: boolean): boolean {
    setError(null);
    if (needsTotp && !/^\d{6}$/.test(totp)) {
      setError("Bu işlem 6 haneli doğrulama kodu istiyor");
      return false;
    }
    return true;
  }

  const act = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => {
      await fn();
    },
    onSuccess: () => {
      setTotp("");
      setReason("");
      onChanged();
    },
    onError: (err) => setError(humanizeError(err, "İşlem başarısız")),
  });

  const withTotp = async (fn: () => Promise<unknown>): Promise<unknown> => {
    await client.confirm(totp);
    return fn();
  };

  return (
    <Card
      title="İnceleme"
      hint={SURFACE_LABELS[kind]}
      flush
    >
      <div className="grid gap-3.5 p-[15px]">
        <div className="rounded-[8px] border border-neutral-800 bg-white/[0.02] px-3.5 py-3 text-sm leading-relaxed break-words text-pretty text-neutral-100">
          {report.body}
        </div>

        <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(130px,1fr))]">
          <Fact label="Şikâyet" value={String(report.report_count)} />
          <Fact label="Durum" value={report.hidden ? "gizli" : "görünür"} />
          <Fact
            label="Kullanıcı"
            value={
              report.display_name ?? report.username ?? report.author_name ?? "adsız kullanıcı"
            }
          />
          <Fact
            label="Geçmiş"
            value={
              user.data
                ? `${user.data.warnings} uyarı · ${user.data.reportedCount} şikâyet`
                : user.isError
                  ? "okunamadı"
                  : "…"
            }
          />
        </div>

        {user.isError && (
          <ErrorState compact error={user.error} onRetry={() => void user.refetch()} />
        )}

        {report.reasons.length > 0 && (
          <p className="text-xs text-neutral-500">
            Gerekçeler: {report.reasons.join(", ")}
          </p>
        )}

        {user.data?.suspended && user.data.suspension && (
          <Banner tone="danger" kicker="askıda">
            {new Date(user.data.suspension.until).toLocaleString("tr-TR")} tarihine kadar —{" "}
            {user.data.suspension.reason ?? "gerekçe yok"}
          </Banner>
        )}

        <div className="grid gap-2 border-t border-neutral-900 pt-3.5">
          <Field label="Gerekçe" hint="Uyarı ve askı bildirimlerinde kullanıcıya bu metin gider.">
            <input
              className="input"
              value={reason}
              maxLength={200}
              onChange={(e) => setReason(e.target.value)}
              placeholder="ör. hakaret içeren dil"
            />
          </Field>

          <div className="flex flex-wrap items-center gap-2">
            {hiddenSupported && (
              <Button
                variant="primary"
                disabled={act.isPending}
                onClick={() =>
                  act.mutate(() =>
                    client.setCommentVisibility(report.id, !report.hidden, kind),
                  )
                }
              >
                {report.hidden ? "Göster" : "İçeriği gizle"}
              </Button>
            )}
            <Button
              disabled={act.isPending}
              onClick={() => act.mutate(() => client.dismissReport(report.id, kind))}
            >
              Temize çıkar
            </Button>
            <Button
              disabled={act.isPending || reason.length < 3}
              title={reason.length < 3 ? "Önce gerekçe yaz" : ""}
              onClick={() =>
                act.mutate(() =>
                  client.warnUser(report.user_id, { reason, commentId: report.id, kind }),
                )
              }
            >
              Kullanıcıyı uyar
            </Button>
          </div>

          <div className="mt-1 grid gap-2 rounded-[8px] border border-neutral-800 bg-white/[0.02] p-3">
            <span className="kicker">Ağır yaptırımlar · TOTP ister</span>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-neutral-400">Askı süresi</span>
              <Chips
                values={DAY_OPTIONS}
                value={String(days)}
                labels={DAY_LABELS}
                onChange={(d) => setDays(Number(d))}
              />
              <input
                className="input mono !w-[92px] text-center"
                aria-label="Doğrulama kodu (6 hane)"
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
                placeholder="kod"
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {user.data?.suspended ? (
                <Button
                  disabled={act.isPending}
                  onClick={() => act.mutate(() => client.unsuspendUser(report.user_id))}
                >
                  Askıyı kaldır
                </Button>
              ) : (
                <ConfirmButton
                  disabled={act.isPending || reason.length < 3}
                  title={reason.length < 3 ? "Önce gerekçe yaz" : undefined}
                  onConfirm={() => {
                    if (guard(true)) {
                      act.mutate(() =>
                        withTotp(() => client.suspendUser(report.user_id, { days, reason })),
                      );
                    }
                  }}
                >
                  {days === 0 ? "Kalıcı askıya al" : `${days} gün askıya al`}
                </ConfirmButton>
              )}
              <ConfirmButton
                disabled={act.isPending}
                onConfirm={() => {
                  if (guard(true)) {
                    act.mutate(() => withTotp(() => client.hideAllComments(report.user_id)));
                  }
                }}
              >
                Tüm yorumlarını gizle
              </ConfirmButton>
              <ConfirmButton
                disabled={act.isPending}
                confirmLabel="Kalıcı silinsin mi?"
                onConfirm={() => {
                  if (guard(true)) {
                    act.mutate(() => withTotp(() => client.deleteComment(report.id, kind)));
                  }
                }}
              >
                Yorumu kalıcı sil
              </ConfirmButton>
            </div>
          </div>

          {act.isPending && <p className="text-xs text-neutral-500">İşleniyor…</p>}
          {error && (
            <p role="alert" className="text-xs text-danger">
              {error}
            </p>
          )}

          <p className="text-xs text-neutral-600">
            Her karar moderasyon günlüğüne yazılır. Silinen metin günlükte saklanır — satır
            gittikten sonra başka kaynak kalmıyor.
          </p>
        </div>

        {user.data && user.data.actions.length > 0 && (
          <div className="grid gap-1.5 border-t border-neutral-900 pt-3">
            <span className="kicker">Bu kullanıcının geçmişi</span>
            {user.data.actions.slice(0, 6).map((a) => (
              <div key={a.id} className="flex items-center gap-2 text-xs">
                <span className="mono min-w-[80px] text-accent-300">{a.action}</span>
                <span className="truncate text-neutral-400">{a.reason ?? a.note ?? "—"}</span>
                <span className="mono ml-auto text-neutral-600">
                  {new Date(a.created_at).toLocaleDateString("tr-TR")}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function SuspensionsTab({ onChanged }: { onChanged: () => void }): ReactNode {
  const toast = useToast();
  const list = useQuery({ queryKey: ["suspensions"], queryFn: () => client.suspensions() });
  const lift = useMutation({
    mutationFn: (userId: string) => client.unsuspendUser(userId),
    onSuccess: () => {
      void list.refetch();
      onChanged();
      toast({ tone: "ok", title: "Askı kaldırıldı" });
    },
    onError: (err) =>
      toast({ tone: "danger", title: "Askı kaldırılamadı", body: humanizeError(err) }),
  });

  return (
    <Card title="Askılar" hint="süresi dolanlar da listede — temizlenebilsin diye" flush>
      {list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : list.isLoading ? (
        <Empty>Yükleniyor…</Empty>
      ) : list.data?.length ? (
        list.data.map((s) => (
          <div key={s.user_id} className="row flex-wrap gap-x-3 gap-y-1">
            <Dot tone={s.active ? "danger" : "idle"} />
            <span className="min-w-0 text-neutral-100 sm:min-w-[150px]">
              {s.display_name ?? s.username ?? "adsız kullanıcı"}
            </span>
            <span className="min-w-0 text-xs text-neutral-500 sm:min-w-[160px]">
              {s.reason ?? "gerekçe yok"}
            </span>
            <span className="mono text-xs text-neutral-600">
              {s.active ? "" : "süresi doldu · "}
              {new Date(s.until).toLocaleString("tr-TR")}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="!ml-auto"
              disabled={lift.isPending}
              loading={lift.isPending && lift.variables === s.user_id}
              onClick={() => lift.mutate(s.user_id)}
            >
              Askıyı kaldır
            </Button>
          </div>
        ))
      ) : (
        <Empty>Askıda kullanıcı yok</Empty>
      )}
    </Card>
  );
}

function LogTab(): ReactNode {
  const log = useQuery({ queryKey: ["moderation-log"], queryFn: () => client.moderationLog(80) });

  return (
    <Card title="Moderasyon günlüğü" hint="kim · ne zaman · neye · neden" flush>
      {log.isError ? (
        <ErrorState error={log.error} onRetry={() => void log.refetch()} />
      ) : log.isLoading ? (
        <Empty>Yükleniyor…</Empty>
      ) : log.data?.length ? (
        log.data.map((a) => (
          <div key={a.id} className="row flex-wrap gap-x-3 gap-y-1 py-2 text-xs">
            <span className="mono text-neutral-600 sm:min-w-[110px]">
              {new Date(a.created_at).toLocaleString("tr-TR", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
            <span className="mono text-accent-300 sm:min-w-[90px]">{a.action}</span>
            <span className="text-neutral-300 sm:min-w-[120px]">
              {a.username ?? a.target_user?.slice(0, 8) ?? "—"}
            </span>
            <span className="min-w-0 truncate text-neutral-500">{a.reason ?? a.note ?? ""}</span>
            {a.until && (
              <span className="mono ml-auto text-2xs text-neutral-600">
                → {new Date(a.until).toLocaleDateString("tr-TR")}
              </span>
            )}
          </div>
        ))
      ) : (
        <Empty>Henüz moderasyon kararı yok</Empty>
      )}
    </Card>
  );
}

/**
 * Toplu işlem çubuğu.
 *
 * Toplu SİLME bilerek yok: geri alınamaz bir işi toplu yapmak, tek yanlış tıkla
 * onarılamaz bir hataya dönüşür. Gizleme ve şikâyet kapatma geri alınabilir.
 */
function BulkBar({
  kind,
  ids,
  hiddenSupported,
  onDone,
}: {
  kind: Surface;
  ids: string[];
  hiddenSupported: boolean;
  onDone: () => void;
}): ReactNode {
  const toast = useToast();
  const bulk = useMutation({
    mutationFn: (op: "dismiss" | "hide") => client.bulkModeration({ kind, ids, op }),
    onSuccess: (_res, op) => {
      toast({
        tone: "ok",
        title: op === "hide" ? `${ids.length} yorum gizlendi` : `${ids.length} şikâyet kapatıldı`,
      });
      onDone();
    },
    onError: (err) => toast({ tone: "danger", title: "Toplu işlem yapılamadı", body: humanizeError(err) }),
  });

  return (
    <>
      <span className="text-xs text-accent-300">{ids.length} seçili</span>
      {hiddenSupported && (
        <ConfirmButton
          size="sm"
          variant="secondary"
          confirmLabel={`${ids.length} yorum gizlensin mi?`}
          disabled={bulk.isPending}
          loading={bulk.isPending && bulk.variables === "hide"}
          onConfirm={() => bulk.mutate("hide")}
        >
          Toplu gizle
        </ConfirmButton>
      )}
      <Button
        size="sm"
        disabled={bulk.isPending}
        loading={bulk.isPending && bulk.variables === "dismiss"}
        onClick={() => bulk.mutate("dismiss")}
      >
        Toplu temize çıkar
      </Button>
    </>
  );
}

/** Otomatik gizleme eşiği ve yasaklı kelime/desen listesi. */
function RulesTab(): ReactNode {
  const queryClient = useQueryClient();
  const [threshold, setThreshold] = useState<string>("");
  const [pattern, setPattern] = useState("");
  const [matchType, setMatchType] = useState<"substring" | "regex">("substring");
  const [action, setAction] = useState<"block" | "flag">("block");
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const settings = useQuery({
    queryKey: ["moderation-settings"],
    queryFn: () => client.moderationSettings(),
  });
  const rules = useQuery({ queryKey: ["blocklist"], queryFn: () => client.blocklist() });

  const current = settings.data?.find((s) => s.key === "auto_hide_threshold");
  const currentValue = current ? String(current.value) : "";

  const saveThreshold = useMutation({
    mutationFn: (value: number) => client.setModerationSetting("auto_hide_threshold", value),
    onSuccess: () => {
      setThreshold("");
      void queryClient.invalidateQueries({ queryKey: ["moderation-settings"] });
      toast({ tone: "ok", title: "Eşik kaydedildi" });
    },
  });

  const addRule = useMutation({
    mutationFn: () => client.addBlockRule({ pattern, matchType, action }),
    onSuccess: () => {
      setPattern("");
      setError(null);
      void rules.refetch();
    },
    onError: (err) => setError(humanizeError(err, "Eklenemedi")),
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      client.toggleBlockRule(id, enabled),
    onSuccess: () => void rules.refetch(),
    onError: (err) => toast({ tone: "danger", title: "Kural değiştirilemedi", body: humanizeError(err) }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => client.deleteBlockRule(id),
    onSuccess: () => {
      void rules.refetch();
      toast({ tone: "ok", title: "Kural silindi" });
    },
    onError: (err) => toast({ tone: "danger", title: "Kural silinemedi", body: humanizeError(err) }),
  });

  return (
    <>
      <Card title="Otomatik gizleme" hint="şikâyet eşiği aşılınca yorum kendiliğinden gizlenir">
        <div className="grid max-w-[520px] gap-2.5">
          <p className="text-xs text-neutral-500">
            Bu eşik MoviePort tarafında koda gömülüydü. Bir yorum bu kadar <strong>açık</strong>{" "}
            şikâyet aldığında otomatik gizlenir. Temize çıkardığın yorumlar bu sayımdan muaf olur.
          </p>
          {settings.isError && (
            <ErrorState compact error={settings.error} onRetry={() => void settings.refetch()} />
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-neutral-400">Şu anki eşik</span>
            <span className="mono text-lg text-accent-300">
              {settings.isError ? "?" : settings.isLoading ? "…" : currentValue || "tanımsız"}
            </span>
            <input
              className="input mono !w-[80px] text-center"
              aria-label="Yeni eşik"
              type="number"
              min={1}
              max={100}
              placeholder="yeni"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
            />
            <Button
              variant="primary"
              disabled={!threshold || Number(threshold) < 1}
              loading={saveThreshold.isPending}
              onClick={() => saveThreshold.mutate(Number(threshold))}
            >
              Kaydet
            </Button>
          </div>
          {saveThreshold.error && (
            <p role="alert" className="text-xs text-danger">
              {humanizeError(saveThreshold.error, "Eşik kaydedilemedi")}
            </p>
          )}
        </div>
      </Card>

      <Card title="Yasaklı kelime ve desenler" hint="mevcut spam filtresinin yanına eklenir">
        <div className="grid gap-2.5">
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto_auto_auto] sm:items-center">
            <input
              className="input mono"
              aria-label="Kelime veya desen"
              placeholder="kelime veya regex deseni"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
            />
            <select
              className="input sm:!w-auto"
              aria-label="Eşleşme türü"
              value={matchType}
              onChange={(e) => setMatchType(e.target.value as "substring" | "regex")}
            >
              <option value="substring">içerir</option>
              <option value="regex">regex</option>
            </select>
            <select
              className="input sm:!w-auto"
              aria-label="Eylem"
              value={action}
              onChange={(e) => setAction(e.target.value as "block" | "flag")}
            >
              <option value="block">engelle</option>
              <option value="flag">gizli doğsun</option>
            </select>
            <Button
              variant="primary"
              disabled={pattern.length < 2}
              loading={addRule.isPending}
              onClick={() => addRule.mutate()}
            >
              Ekle
            </Button>
          </div>
          <p className="text-2xs text-neutral-600">
            <strong>engelle</strong>: yorum hiç yazılmaz, kullanıcı hata görür ·{" "}
            <strong>gizli doğsun</strong>: yazılır ama görünmez, kuyrukta incelersin. Regex
            desenleri kaydedilmeden önce sınanır.
          </p>
          {error && (
            <p role="alert" className="text-xs text-danger">
              {error}
            </p>
          )}
        </div>
      </Card>

      <Card title={rules.data ? `Kurallar · ${rules.data.length}` : "Kurallar"} flush>
        {rules.isError ? (
          <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />
        ) : rules.isLoading ? (
          <Empty>Yükleniyor…</Empty>
        ) : rules.data?.length ? (
          rules.data.map((rule) => (
            <div key={rule.id} className="row flex-wrap gap-x-3 gap-y-1">
              <Dot tone={rule.enabled ? (rule.action === "block" ? "danger" : "warn") : "idle"} />
              <span className="mono min-w-0 text-xs break-all text-neutral-100 sm:min-w-[180px]">
                {rule.pattern}
              </span>
              <span className="text-2xs text-neutral-500">
                {rule.match_type === "regex" ? "regex" : "içerir"} ·{" "}
                {rule.action === "block" ? "engelle" : "gizli doğsun"}
                {rule.enabled ? "" : " · kapalı"}
              </span>
              <span className="ml-auto flex items-center gap-2">
                <span className="mono text-2xs text-neutral-600">
                  {new Date(rule.created_at).toLocaleDateString("tr-TR")}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={toggle.isPending}
                  loading={toggle.isPending && toggle.variables?.id === rule.id}
                  onClick={() => toggle.mutate({ id: rule.id, enabled: !rule.enabled })}
                >
                  {rule.enabled ? "Kapat" : "Aç"}
                </Button>
                <ConfirmButton
                  variant="ghost"
                  size="sm"
                  confirmLabel="Silinsin mi?"
                  loading={remove.isPending && remove.variables === rule.id}
                  onConfirm={() => remove.mutate(rule.id)}
                >
                  Sil
                </ConfirmButton>
              </span>
            </div>
          ))
        ) : (
          <Empty>Kural yok — mevcut spam filtresi (URL engelleme) yine de çalışıyor</Empty>
        )}
      </Card>
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="grid min-w-0 gap-1">
      <span className="kicker">{label}</span>
      <span className="truncate text-sm text-neutral-200">{value}</span>
    </div>
  );
}
